import { ApiService, ApiError } from "../utils/ApiService.js";
import { SesionService } from "./SesionService.js";
import { Fechas } from "../utils/Fechas.js";

// Error de negocio con mensaje listo para mostrar (no es una falla tecnica).
export class ErrorNegocio extends Error {
  constructor(mensaje) {
    super(mensaje);
    this.name = "ErrorNegocio";
  }
}

// Unico modulo que conoce los endpoints de Empleados y Vacaciones y el shape de sus respuestas.
export class VacacionesService {
  /**
   * GET Empleados?Legajo=&Documento=
   * Devuelve { valido, empleado } — la API responde 200 siempre y discrimina por `mensaje`.
   */
  static async validarEmpleado(legajo, dni) {
    const qs = new URLSearchParams({ Legajo: legajo, Documento: dni });
    const data = await ApiService.get("Empleados?" + qs.toString(), { dedupe: false });

    if (data?.mensaje === "valido") {
      return {
        valido: true,
        empleado: {
          codigoLegajo: data.codigoLegajo,
          nombre: data.nombre,
          diasDisponibles: data.diasDisponibles,
          fotoVersion: data.fotoVersion ?? null,
        },
      };
    }
    if (data?.mensaje === "invalido") return { valido: false };

    // Cualquier otro `mensaje` es un "Error: ..." del backend
    throw new Error(data?.mensaje || "Respuesta inesperada del servidor");
  }

  /** GET Vacaciones/{codigoLegajo} → lista (null si el legajo no existe). */
  static async listar() {
    // El codigo (Base64 de digitos) solo puede traer "=", que es valido en un path
    // segment; se manda sin encodear, igual que el front anterior ya probado en IIS.
    const data = await ApiService.get("Vacaciones/" + this.codigo());
    if (data === null) throw new ErrorNegocio("No encontramos tus datos. Volvé a ingresar.");
    return (data || []).map((v) => this.mapear(v));
  }

  /**
   * GET Vacaciones/{codigoLegajo}/resumen → { diasDisponibles, diasComprometidos, diasEfectivos }.
   * Lo calcula la API porque los pedidos ya gozados pero no liquidados tambien comprometen
   * dias y no vienen en el listado. Refresca los dias disponibles de la sesion.
   */
  static async resumen() {
    const data = await ApiService.get("Vacaciones/" + this.codigo() + "/resumen");
    const resumen = {
      diasDisponibles: data?.diasDisponibles ?? 0,
      diasComprometidos: data?.diasComprometidos ?? 0,
      diasEfectivos: data?.diasEfectivos ?? 0,
    };
    SesionService.actualizarDias(resumen.diasDisponibles);
    return resumen;
  }

  /** GET Vacaciones/{codigoLegajo}/historial → pedidos terminados en el ultimo anio, mas reciente primero. */
  static async historial() {
    const data = await ApiService.get("Vacaciones/" + this.codigo() + "/historial");
    return (data || []).map((v) => this.mapear(v));
  }

  /** GET Vacaciones?c=&v= → una vacacion. */
  static async obtener(id) {
    const qs = new URLSearchParams({ c: this.codigo(), v: id });
    try {
      const data = await ApiService.get("Vacaciones?" + qs.toString());
      return this.mapear(data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        throw new ErrorNegocio("Ese pedido ya no existe.");
      }
      throw error;
    }
  }

  /**
   * POST Vacaciones — id=0 alta, id>0 edicion (asi lo resuelve el backend).
   * Traduce los status de negocio a ErrorNegocio; el resto sube como error tecnico.
   */
  static async guardar({ id, cobro, desde, hasta, codigoHabilitacion = null }) {
    const datos = {
      Id: id || 0,
      CodigoLegajo: this.codigo(),
      Cobro: cobro,
      Desde: Fechas.aIsoApi(desde),
      Hasta: Fechas.aIsoApi(hasta),
      // Solo en pedidos urgentes (menos anticipacion que la minima): lo genera RRHH en Claves Celular
      CodigoHabilitacion: codigoHabilitacion || null,
    };

    try {
      await ApiService.post("Vacaciones", datos, { dedupe: false });
    } catch (error) {
      if (error instanceof ApiError) {
        switch (error.status) {
          case 400:
            if (error.body?.motivo === "fechaPasada") throw new ErrorNegocio("La fecha de salida no puede ser anterior a hoy.");
            throw new ErrorNegocio("No podés programar más días de los que tenés disponibles.");
          case 403:
            throw new ErrorNegocio(
              codigoHabilitacion
                ? "El código de habilitación no es válido para esta fecha. Revisá que esté bien escrito y que RRHH lo haya generado hoy."
                : "Para pedir vacaciones con tan poca anticipación necesitás una habilitación de RRHH."
            );
          case 404:
            throw new ErrorNegocio("No encontramos tus datos. Volvé a ingresar.");
          case 422:
            throw new ErrorNegocio("Ya tenés vacaciones cargadas que se superponen con esas fechas.");
        }
      }
      throw error;
    }
    ApiService.invalidate("Vacaciones");
  }

  /** DELETE Vacaciones?c=&v= */
  static async borrar(id) {
    const qs = new URLSearchParams({ c: this.codigo(), v: id });
    try {
      await ApiService.delete("Vacaciones?" + qs.toString(), { dedupe: false });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400) {
        throw new ErrorNegocio("Esas vacaciones ya no se pueden borrar.");
      }
      throw error;
    }
    ApiService.invalidate("Vacaciones");
  }

  static codigo() {
    const emp = SesionService.empleado();
    if (!emp) throw new ErrorNegocio("La sesión expiró. Volvé a ingresar.");
    return emp.codigoLegajo;
  }

  // Shape que consumen las pantallas. `estado` viene armado del backend
  // ("Pendiente", "Pendiente RRHH", "Autorizada", "Rechazada", "Rechazada RRHH").
  static mapear(v) {
    const estado = v.estado || "";
    return {
      id: v.id,
      cobro: v.cobro,
      cobroDesc: v.cobroDesc,
      desde: v.desde,
      hasta: v.hasta,
      fPedida: v.fPedida,
      dias: v.dias ?? Fechas.diasCorridos(v.desde, v.hasta),
      estado,
      editarHabilitado: !!v.editarHabilitado,
      borrarHabilitado: !!v.borrarHabilitado,
    };
  }
}
